import { useTranslation as useInterfaceTranslation } from "react-i18next";
/**
 * Pricing panel for the new-order screen (suppliers only).
 * Prices are computed live in the browser with the same engine the server uses;
 * when the order is saved (order_id exists) the selection is PATCHed so the
 * server writes OrderItem.unit_price.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchCustomerPricing, fetchSupplierPricing } from "Redux/middlwares/supplierPricing";
import { getOrderPricing, setOrderPriceList } from "Services/supplier-pricing";
import { priceOrder } from "../../utils/supplier-pricing-engine";
import { PBtn, PPill, PSelect, fmt } from "./ui";
import FinancialAnalysisModal from "./financial-analysis-modal";
import QuoteModal from "./quote-modal";

type Props = {
  orderId?: number | null;
  supplierCustomerId?: number | null;
  business?: any;
};

export default function OrderPricingPanel({ orderId, supplierCustomerId, business }: Props) {
  const { t: uiT } = useInterfaceTranslation("interface");
  const { t } = useTranslation(["supplierPricing"]);
  const dispatch = useAppDispatch();
  const priceList = useAppSelector((s) => s.supplierPricing.saved);
  const customLists = useAppSelector((s) => s.supplierPricing.customLists);
  const customerPricing = useAppSelector((s) => s.supplierPricing.customerPricing);
  const rebars = useAppSelector((s) => s.rebar.currentOrderRebars);
  const nets = useAppSelector((s) => s.net.currentOrderNets);
  const piles = useAppSelector((s) => s.pile.currentOrderPiles);
  const [key, setKey] = useState("default");
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    if (!priceList) dispatch(fetchSupplierPricing());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (next: string) => {
    if (!orderId) return;
    try {
      await setOrderPriceList(orderId, next);
      setKey(next);
    } catch (e) {
      toast.error("עדכון המחירון בהזמנה נכשל");
    }
  };

  // A saved order keeps the explicit server selection; customer lists are never auto-applied.
  useEffect(() => {
    if (!orderId) {
      setKey("default");
      return;
    }
    getOrderPricing(orderId)
      .then((r: any) => setKey(r.data.price_list))
      .catch(() => setKey("default"));
  }, [orderId]);

  useEffect(() => {
    if (supplierCustomerId) dispatch(fetchCustomerPricing(supplierCustomerId));
  }, [supplierCustomerId, dispatch]);

  const selectedCustomerId = key.startsWith("customer:") ? Number(key.split(":")[1]) : null;
  const customer = selectedCustomerId ? customerPricing[selectedCustomerId] || customLists.find((c) => c.customer === selectedCustomerId) || null : null;

  const result = useMemo(() => {
    if (!priceList) return null;
    return priceOrder([...rebars, ...nets, ...piles], priceList, customer, supplierCustomerId || null);
  }, [priceList, rebars, nets, piles, customer, supplierCustomerId]);

  if (!priceList || !result) return null;
  const { totals } = result;
  const empty = result.items.length === 0;

  const row = (label: string, value: number, cls = "") => (
    <div className={`eb-pr-summary__row ${cls}`}>
      <span>{label}</span>
      <span>{fmt(value)} {uiT("NIS")} </span>
    </div>
  );

  return (
    <div className="eb-pr eb-pr-pricing-panel">
      <div className="eb-pr-side">
        <div className="eb-pr-row">
          <span className="eb-pr-label">{t("order.priceList")}</span>
          <PSelect
            width={260}
            value={key}
            onChange={choose}
            disabled={!orderId}
            ariaLabel={t("order.priceList")}
            options={[
              { value: "default", label: t("tabs.default") },
              ...customLists.filter((c) => c.customer === supplierCustomerId).map((c) => ({ value: `customer:${c.customer}`, label: c.customerName || `#${c.customer}` })),
            ]}
          />
        </div>
        {totals.missingPrices.length > 0 && <div className="eb-pr-warn">{t("order.missingPrices", { count: totals.missingPrices.length })}</div>}
        {!orderId && !empty && <PPill tone="amber">{t("order.saveToApplyServer")}</PPill>}
        <div className="eb-pr-row">
          <PBtn kind="quote" disabled={empty || !orderId || totals.missingPrices.length > 0} title={!orderId ? t("order.saveToApplyServer") : undefined} onClick={() => setQuoteOpen(true)}>
            {t("order.createQuote")}
          </PBtn>
          <PBtn kind="secondary" disabled={empty} onClick={() => setAnalysisOpen(true)}>
            {t("order.analysis")}
          </PBtn>
        </div>
      </div>

      <div className="eb-pr-summary">
        <span className="eb-pr-summary__tab">{t("order.summary")}</span>
        <div className="eb-pr-summary__body">
          {row(t("order.material", { tons: totals.weightTon.toFixed(3) }), totals.material)}
          {totals.bend > 0 && row(t("order.bend"), totals.bend)}
          {totals.cut > 0 && row(t("order.cut"), totals.cut)}
          {totals.units > 0 && row(t("order.units"), totals.units)}
          {row(t("order.transport"), totals.transport)}
          {totals.discount !== 0 && row(t("order.discount", { pct: customer?.discountPercent || 0 }), totals.discount)}
          {row(t("order.subtotal"), totals.subtotal, "eb-pr-summary__row--strong")}
          {row(t("order.vat", { pct: totals.vatPercent }), totals.vat)}
          {row(t("order.total"), totals.total, "eb-pr-summary__row--total")}
        </div>
      </div>

      <FinancialAnalysisModal show={analysisOpen} onHide={() => setAnalysisOpen(false)} result={result} />
      {orderId && (
        <QuoteModal
          show={quoteOpen}
          onHide={() => setQuoteOpen(false)}
          orderId={orderId}
          result={result}
          customerId={supplierCustomerId || null}
          business={business}
          paymentTerms={customer?.paymentTerms}
          discountPercent={customer?.discountPercent}
          validityDays={priceList.quoteValidityDays}
        />
      )}
    </div>
  );
}
